import Dexie, { type EntityTable } from 'dexie';
import type { AdminMissionListDTO, PlayerStateDTO } from '@shared/api.js';
import type { AppSettings } from '@shared/types.js';

/**
 * מטמון מצב שחקן — עותק אחרון של PlayerStateDTO, לצביעה מיידית ולעבודה Offline.
 * שורה יחידה (id קבוע).
 */
export interface CachedPlayerState {
  id: 1;
  data: PlayerStateDTO;
  fetchedAt: string;
}

/** כוונת פתיחת משימה שממתינה לאישור השרת (נוצרה Offline או שקריאת הרשת נכשלה) */
export interface PendingOpen {
  clientEventId: string;
  missionId: string;
  method: 'time' | 'location' | 'manual';
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  isTest: boolean;
  createdAt: string;
  attempts: number;
  lastError: string | null;
  lastAttemptAt: string | null;
}

/**
 * פתק ביומן השדה שממתין לאישור השרת (נוצר Offline או שהרשת נכשלה).
 * imageBlob — התמונה הגולמית (Dexie תומך אחסון Blob ישירות); מועלית ברקע
 * ברגע שיש רשת, ואז מוחלפת ב-imageUrl (URL בשרת) כדי לא לשמור bytes כפולים.
 */
export interface PendingMessage {
  id: string;
  text: string;
  imageBlob: Blob | null;
  imageUrl: string | null;
  missionId: string | null;
  isTest: boolean;
  createdAt: string;
  attempts: number;
  lastError: string | null;
}

/** פעולת מנהל בתור — נוצרה Offline וממתינה לדחיפה לשרת */
export interface AdminQueueOp {
  id: string;
  type: 'CREATE_MISSION' | 'UPDATE_MISSION' | 'DELETE_MISSION' | 'MANUAL_UNLOCK' | 'UPDATE_SETTINGS';
  payload: unknown;
  baseVersion?: number;
  createdAt: string;
  attempts: number;
  lastError: string | null;
  /** תיאור קריא-אדם לתצוגה ב"ממתין לסנכרון" */
  label: string;
}

/** מטמון רשימת המשימות המלאה של המנהל, לעבודת ניהול Offline */
export interface CachedAdminMissions {
  id: 1;
  data: AdminMissionListDTO;
  fetchedAt: string;
}

export interface CachedAdminSettings {
  id: 1;
  data: AppSettings;
  fetchedAt: string;
}

class AgentsDB extends Dexie {
  playerState!: EntityTable<CachedPlayerState, 'id'>;
  pendingOpens!: EntityTable<PendingOpen, 'clientEventId'>;
  pendingMessages!: EntityTable<PendingMessage, 'id'>;
  adminQueue!: EntityTable<AdminQueueOp, 'id'>;
  adminMissions!: EntityTable<CachedAdminMissions, 'id'>;
  adminSettings!: EntityTable<CachedAdminSettings, 'id'>;

  constructor() {
    super('pmr-agents-db');
    this.version(1).stores({
      playerState: 'id',
      pendingOpens: 'clientEventId, missionId',
      adminQueue: 'id, createdAt',
      adminMissions: 'id',
      adminSettings: 'id',
    });
    this.version(2).stores({
      pendingMessages: 'id, createdAt',
    });
  }
}

export const db = new AgentsDB();

export async function getCachedPlayerState(): Promise<PlayerStateDTO | null> {
  const row = await db.playerState.get(1);
  return row?.data ?? null;
}

export async function setCachedPlayerState(data: PlayerStateDTO): Promise<void> {
  await db.playerState.put({ id: 1, data, fetchedAt: new Date().toISOString() });
}
